import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Globe, ShieldCheck, Loader2, Copy, Plus, CircleDashed } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function SitesManager() {
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState("");
  const [starting, setStarting] = useState(false);
  const [pending, setPending] = useState(null); // {host, txt_record}
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => api.get("/domains").then((r) => setDomains(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const start = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setStarting(true);
    try {
      const { data } = await api.post("/domains/verify/start", { domain });
      setPending(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start verification.");
    } finally {
      setStarting(false);
    }
  };

  const check = async () => {
    if (!pending) return;
    setChecking(true);
    try {
      const { data } = await api.post("/domains/verify/check", { domain: pending.host });
      if (data.verified) {
        toast.success(`${pending.host} verified! Deep & scheduled scans enabled.`);
        setPending(null);
        setDomain("");
        load();
      } else {
        toast.error("TXT record not found yet. DNS can take a few minutes to propagate.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed.");
    } finally {
      setChecking(false);
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied to clipboard"); };

  return (
    <div className="bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="sites-manager">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-cyanbrand" />
        <h3 className="font-heading font-semibold">Verified domains</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">Verify ownership to unlock deep & scheduled monitoring scans.</p>

      <form onSubmit={start} className="flex items-center bg-[#030812] border border-slate-700 rounded-md p-1.5 focus-within:border-cyanbrand transition-all">
        <Globe size={18} className="text-slate-500 ml-2 shrink-0" />
        <input
          data-testid="domain-verify-input"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="yourwebsite.com"
          className="flex-1 bg-transparent outline-none px-2 py-1.5 text-white placeholder:text-slate-600 font-mono text-sm"
        />
        <button
          data-testid="domain-verify-start-button"
          disabled={starting}
          className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white text-sm px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
        >
          {starting ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add
        </button>
      </form>

      {pending && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 bg-[#030812] border border-cyanbrand/30 rounded-md p-4">
          <p className="text-sm text-slate-300">Add this <b>TXT</b> record to <span className="font-mono text-cyanbrand">{pending.host}</span>:</p>
          <div className="flex items-center gap-2 mt-2 bg-[#0C1424] border border-slate-700 rounded px-3 py-2">
            <code className="flex-1 text-xs text-cyanbrand font-mono break-all">{pending.txt_record}</code>
            <button onClick={() => copy(pending.txt_record)} className="text-slate-400 hover:text-white"><Copy size={15} /></button>
          </div>
          <button
            data-testid="domain-verify-check-button"
            onClick={check}
            disabled={checking}
            className="mt-3 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold text-sm px-4 py-2 rounded-md transition-all disabled:opacity-60"
          >
            {checking ? "Checking DNS…" : "Verify now"}
          </button>
        </motion.div>
      )}

      {domains.length > 0 && (
        <div className="mt-4 space-y-2">
          {domains.map((d) => (
            <div key={d.host} className="flex items-center justify-between text-sm py-1.5 border-t border-slate-800/60">
              <span className="font-mono text-slate-300">{d.host}</span>
              {d.verified ? (
                <span className="flex items-center gap-1.5 text-green-400 text-xs"><ShieldCheck size={14} /> Verified</span>
              ) : (
                <span className="flex items-center gap-1.5 text-slate-500 text-xs"><CircleDashed size={14} /> Pending</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
